// src/pages/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Avatar,
  CircularProgress,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Badge
} from '@mui/material';
import {
  People as UsersIcon,
  Event as EventsIcon,
  Announcement as AnnouncementsIcon,
  Book as ResourcesIcon,
  Class as CoursesIcon,
  BarChart as StatsIcon,
  Group as GroupIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Article as BlogIcon,
  HowToReg as HowToRegIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  NotificationsActive as NotificationsActiveIcon,
  School as SchoolIcon,
  Public as WebIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  ShoppingBag as ShoppingBagIcon,
  Cake as CakeIcon,
  FolderSpecial as FolderSpecialIcon,
  EventRepeat as EventRepeatIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ChatIcon from '@mui/icons-material/Chat';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import GavelIcon from '@mui/icons-material/Gavel';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';

function adjustColor(color, amount) {
  if (!color || !color.startsWith('#')) return color;
  return (
    '#' +
    color
      .replace(/^#/, '')
      .replace(/../g, (cc) =>
        ('0' + Math.min(255, Math.max(0, parseInt(cc, 16) + amount)).toString(16)).slice(-2)
      )
  );
}

function DashboardCard({ title, icon, path, count, color, draggable, onDragStart, badge }) {
  const navigate = useNavigate();

  return (
    <Card
      draggable={draggable}
      onDragStart={onDragStart}
      sx={{
        cursor: draggable ? 'grab' : 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        background: color
          ? `linear-gradient(135deg, ${color} 0%, ${adjustColor(color, -20)} 100%)`
          : 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
        color: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 20px rgba(0,0,0,0.15)'
        }
      }}
      onClick={() => navigate(path)}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '24px'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {typeof count === 'number' ? count : (count || 0)}
            </Typography>
          </Box>

          <Badge badgeContent={badge || 0} color="error" invisible={!badge}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 48, height: 48 }}>
              {icon}
            </Avatar>
          </Badge>
        </Box>

        <Button
          variant="text"
          size="small"
          sx={{
            color: 'white',
            alignSelf: 'flex-start',
            mt: 2,
            px: 0,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          Voir détails <ArrowForwardIcon sx={{ ml: 0.5, fontSize: 16 }} />
        </Button>
      </CardContent>
    </Card>
  );
}

const ICON_BY_ID = {
  'card-users': <GroupIcon />,
  'card-teachers': <SchoolIcon />,
  'card-students': <SchoolIcon />,
  'card-parents': <UsersIcon />,
  'card-attendance': <HowToRegIcon />,
  'card-student-evolutions': <TrendingUpIcon />,
  'card-courses': <CoursesIcon />,
  'card-events': <EventsIcon />,
  'card-registrations': <HowToRegIcon />,
  'card-subscriptions': <EventRepeatIcon />,
  'card-crew-auditions': <HowToRegIcon />,
  'card-announcements': <AnnouncementsIcon />,
  'card-blog': <BlogIcon />,
  'card-resources': <ResourcesIcon />,
  'card-website': <WebIcon />,
  'card-school-rules': <GavelIcon />,
  'card-contacts': <UsersIcon />,
  'card-quotes': <DescriptionIcon />,
  'card-payments': <PaymentIcon />,
  'card-accounting': <AccountBalanceIcon />,
  'card-goals': <HowToVoteIcon />,
  'card-chats': <ChatIcon />,
  'card-notifications': <NotificationsActiveIcon />,
  'card-stats': <StatsIcon />,
  'card-shop': <ShoppingCartIcon />,
  'card-product-orders': <ShoppingBagIcon />,
  'card-birthdays-today': <CakeIcon />,
  'card-documents': <FolderSpecialIcon />
};

const defaultGroups = [
  {
    id: 'group-users',
    title: 'Utilisateurs',
    items: [
      { id: 'card-users', title: 'Utilisateurs', icon: <GroupIcon />, path: '/users', count: 0, color: '#7C3AED' },
      { id: 'card-teachers', title: 'Professeurs', icon: <SchoolIcon />, path: '/teachers', count: 0, color: '#4F46E5' },
      { id: 'card-students', title: 'Élèves', icon: <SchoolIcon />, path: '/students', count: 0, color: '#4F46E5' },
      { id: 'card-parents', title: 'Parents', icon: <UsersIcon />, path: '/parents', count: 0, color: '#8B5CF6' },
      { id: 'card-attendance', title: 'Gérer les présences', icon: <HowToRegIcon />, path: '/attendance', count: 'Voir', color: '#4CAF50' },
      { id: 'card-student-evolutions', title: 'Suivi individuel', icon: <TrendingUpIcon />, path: '/student-evolutions', count: 0, color: '#7C3AED' }
    ]
  },
  {
    id: 'group-courses',
    title: 'Cours & Événements',
    items: [
      { id: 'card-courses', title: 'Cours', icon: <CoursesIcon />, path: '/courses', count: 0, color: '#0EA5E9' },
      { id: 'card-events', title: 'Événements', icon: <EventsIcon />, path: '/events', count: 0, color: '#10B981' },
      { id: 'card-registrations', title: 'Inscriptions', icon: <HowToRegIcon />, path: '/registrations', count: 0, color: '#3B82F6' },
      { id: 'card-subscriptions', title: 'Abonnements', icon: <EventRepeatIcon />, path: '/subscriptions', count: 0, color: '#0891B2' },
      { id: 'card-crew-auditions', title: 'Auditions Crew', icon: <HowToRegIcon />, path: '/crew-auditions', count: 0, color: '#7C3AED' }
    ]
  },
  {
    id: 'group-content',
    title: 'Contenu & Communication',
    items: [
      { id: 'card-announcements', title: 'Annonces', icon: <AnnouncementsIcon />, path: '/announcements', count: 0, color: '#EF4444' },
      { id: 'card-blog', title: 'Blog', icon: <BlogIcon />, path: '/website/blog', count: 0, color: '#8B5CF6' },
      { id: 'card-resources', title: 'Ressources', icon: <ResourcesIcon />, path: '/resources', count: 0, color: '#F59E0B' },
      { id: 'card-website', title: 'Gestion du Site', icon: <WebIcon />, path: '/website', count: 'Éditer', color: '#8B5CF6' },
      { id: 'card-school-rules', title: 'Règlement de l\'école', icon: <GavelIcon />, path: '/school-rules', count: 'Éditer', color: '#4F46E5' },
      { id: 'card-shop', title: 'Boutique', icon: <ShoppingCartIcon />, path: '/shop', count: 0, color: '#F59E0B' },
      { id: 'card-product-orders', title: 'Commandes', icon: <ShoppingBagIcon />, path: '/shop/orders', count: 0, color: '#EC4899' }
    ]
  },
  {
    id: 'group-business',
    title: 'Commercial & Administration',
    items: [
      { id: 'card-contacts', title: 'Contacts', icon: <UsersIcon />, path: '/contacts', count: 0, color: '#8B5CF6' },
      { id: 'card-quotes', title: 'Devis', icon: <DescriptionIcon />, path: '/quotes', count: 0, color: '#F59E0B' },
      { id: 'card-payments', title: 'Paiements', icon: <PaymentIcon />, path: '/payments', count: 0, color: '#10B981' },
      { id: 'card-accounting', title: 'Comptabilité', icon: <AccountBalanceIcon />, path: '/accounting', count: '0 €', color: '#EF4444' },
      { id: 'card-documents', title: 'Documents', icon: <FolderSpecialIcon />, path: '/documents', count: 0, color: '#7C3AED' }
    ]
  },
  {
    id: 'group-other',
    title: 'Autres Fonctionnalités',
    items: [
      { id: 'card-goals', title: 'Objectifs', icon: <HowToVoteIcon />, path: '/goals', count: 0, color: '#8B5CF6' },
      { id: 'card-chats', title: 'Chats', icon: <ChatIcon />, path: '/chats', count: 0, color: '#0EA5E9' },
      { id: 'card-notifications', title: 'Notifications', icon: <NotificationsActiveIcon />, path: '/notifications', count: 0, color: '#F59E0B' },
      { id: 'card-birthdays-today', title: 'Anniversaires du jour', icon: <CakeIcon />, path: '/students', count: 0, color: '#EC4899' },
      { id: 'card-stats', title: 'Statistiques', icon: <StatsIcon />, path: '/stats', count: 'Voir', color: '#10B981' }
    ]
  }
];

const stripIcons = (groups) => groups.map((group) => ({
  ...group,
  items: group.items.map(({ icon, ...item }) => item)
}));

const reAddIcons = (groups) => groups.map((group) => ({
  ...group,
  items: group.items.map((item) => ({
    ...item,
    icon: ICON_BY_ID[item.id] || <NotificationsActiveIcon />
  }))
}));

const isValidGroups = (groups) => Array.isArray(groups) && groups.length > 0 && groups.every(
  (group) => group && typeof group.id === 'string' && Array.isArray(group.items)
);

const mergeMissingDefaultCards = (savedGroups) => {
  const existingIds = new Set(savedGroups.flatMap((group) => group.items.map((item) => item.id)));
  const next = savedGroups.map((group) => ({ ...group, items: [...group.items] }));

  defaultGroups.forEach((defaultGroup) => {
    let targetGroup = next.find((group) => group.id === defaultGroup.id);
    if (!targetGroup) {
      targetGroup = { ...defaultGroup, items: [] };
      next.push(targetGroup);
    }

    defaultGroup.items.forEach((item) => {
      if (!existingIds.has(item.id)) {
        targetGroup.items.push(item);
        existingIds.add(item.id);
      }
    });
  });

  return next;
};

const persistGroups = (groups) => {
  try {
    if (!isValidGroups(groups)) return;
    localStorage.setItem('dashboardGroups', JSON.stringify(stripIcons(groups)));
  } catch {}
};

const applyCounts = (groups, stats, contactsCount, quotesCount) =>
  groups.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      switch (item.id) {
        case 'card-users': return { ...item, count: stats.users };
        case 'card-teachers': return { ...item, count: stats.teachers };
        case 'card-students': return { ...item, count: stats.students };
        case 'card-parents': return { ...item, count: stats.parents };
        case 'card-attendance': return { ...item, count: stats.attendance > 0 ? stats.attendance : 'Voir' };
        case 'card-student-evolutions': return { ...item, count: stats.studentEvolutions || 0 };
        case 'card-courses': return { ...item, count: stats.courses };
        case 'card-events': return { ...item, count: stats.events };
        case 'card-registrations': return { ...item, count: stats.registrations };
        case 'card-subscriptions': return { ...item, count: stats.subscriptions, badge: stats.subscriptionsOverdue || 0 };
        case 'card-crew-auditions': return { ...item, count: stats.crewAuditions || 0, badge: stats.newCrewAuditions || 0 };
        case 'card-announcements': return { ...item, count: stats.announcements };
        case 'card-resources': return { ...item, count: stats.resources };
        case 'card-blog': return { ...item, count: stats.blog };
        case 'card-website': return { ...item, count: 'Éditer' };
        case 'card-contacts': return { ...item, count: contactsCount };
        case 'card-quotes': return { ...item, count: quotesCount };
        case 'card-payments': return { ...item, count: stats.payments || 0 };
        case 'card-accounting': return { ...item, count: stats.accounting || '0 €' };
        case 'card-goals': return { ...item, count: stats.goals };
        case 'card-chats': return { ...item, count: stats.chats || 0 };
        case 'card-notifications': return { ...item, count: stats.unreadNotifications || 0, badge: stats.unreadNotifications || 0 };
        case 'card-stats': return { ...item, count: 'Voir' };
        case 'card-shop': return { ...item, count: stats.shop || 0 };
        case 'card-product-orders': return { ...item, count: stats.productOrders || 0, badge: stats.pendingProductOrders || 0 };
        case 'card-birthdays-today': return { ...item, count: stats.todayBirthdays || 0, badge: stats.todayBirthdays || 0 };
        case 'card-documents': return { ...item, count: stats.pendingDocumentRequests || 0, badge: stats.pendingDocumentRequests || 0 };
        default: return item;
      }
    })
  }));

function parseBirthDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getUpcomingBirthdays(users) {
  const today = new Date();
  const nextMonth = new Date();
  nextMonth.setDate(today.getDate() + 30);

  return users
    .filter((user) => {
      const birthDate = parseBirthDate(user.dateOfBirth);
      if (!birthDate) return false;

      const birthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
      if (birthday < today) birthday.setFullYear(today.getFullYear() + 1);

      return birthday >= today && birthday <= nextMonth;
    })
    .sort((a, b) => parseBirthDate(a.dateOfBirth) - parseBirthDate(b.dateOfBirth));
}

function getTodaysBirthdays(users) {
  const today = new Date();
  return users.filter((user) => {
    const birthDate = parseBirthDate(user.dateOfBirth);
    return birthDate &&
      birthDate.getDate() === today.getDate() &&
      birthDate.getMonth() === today.getMonth();
  });
}

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    courses: 0,
    events: 0,
    announcements: 0,
    resources: 0,
    blog: 0,
    teachers: 0,
    students: 0,
    parents: 0,
    registrations: 0,
    crewAuditions: 0,
    newCrewAuditions: 0,
    goals: 0,
    attendance: 0,
    shop: 0,
    productOrders: 0,
    pendingProductOrders: 0,
    unreadNotifications: 0,
    todayBirthdays: 0,
    pendingDocumentRequests: 0,
    subscriptions: 0,
    subscriptionsOverdue: 0,
    studentEvolutions: 0
  });
  const [contactsCount, setContactsCount] = useState(0);
  const [quotesCount, setQuotesCount] = useState(0);
  const [cardGroups, setCardGroups] = useState([]);
  const [editGroupDialog, setEditGroupDialog] = useState({ open: false, group: null });
  const [newGroupTitle, setNewGroupTitle] = useState('');

  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [recentCourses, setRecentCourses] = useState([]);

  const dragDataRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const usersSnapshot = await getDocs(query(collection(db, 'users')));
        const usersData = usersSnapshot.docs.map((document) => ({ id: document.id, ...document.data() }));

        const usersCount = usersData.length;
        const teachersCount = usersData.filter((u) => ['teacher', 'professeur', 'Professeur'].includes(u.role)).length;
        const studentsCount = usersData.filter((u) => ['student', 'élève', 'eleve', 'Élève', 'Élève adulte'].includes(u.role)).length;
        const parentsCount = usersData.filter((u) => ['parent', 'Parent'].includes(u.role)).length;

        const [
          coursesSnapshot,
          eventsSnapshot,
          announcementsSnapshot,
          resourcesSnapshot,
          blogSnapshot,
          contactsSnapshot,
          quotesSnapshot,
          goalsSnapshot,
          registrationsSnapshot,
          crewAuditionsSnapshot,
          newCrewAuditionsSnapshot,
          attendanceSnapshot,
          productsSnapshot,
          productOrdersSnapshot,
          pendingProductOrdersSnapshot,
          notificationsSnapshot,
          pendingDocRequestsSnapshot,
          studentEvolutionsSnapshot
        ] = await Promise.all([
          getDocs(query(collection(db, 'dance_courses'))),
          getDocs(query(collection(db, 'events'))),
          getDocs(query(collection(db, 'announcements'))),
          getDocs(query(collection(db, 'resources'))),
          getDocs(query(collection(db, 'blog_posts'))),
          getDocs(query(collection(db, 'contacts'))),
          getDocs(query(collection(db, 'quotes'))),
          getDocs(query(collection(db, 'objectives'))),
          getDocs(query(collection(db, 'registrations'))),
          getDocs(query(collection(db, 'crew_auditions'))),
          getDocs(query(collection(db, 'crew_auditions'), where('status', '==', 'new'))),
          getDocs(query(collection(db, 'attendance'))),
          getDocs(query(collection(db, 'products'))),
          getDocs(query(collection(db, 'product_orders'))),
          getDocs(query(collection(db, 'product_orders'), where('status', '==', 'pending'))),
          getDocs(query(collection(db, 'notifications'), orderBy('createdAt', 'desc'), limit(50))),
          getDocs(query(collection(db, 'document_requests'), where('status', '==', 'pending'))),
          getDocs(query(collection(db, 'student_evolution_profiles')))
        ]);

        const [recentRegSnap, recentAnnSnap, recentCourseSnap] = await Promise.all([
          getDocs(query(collection(db, 'registrations'), orderBy('createdAt', 'desc'), limit(5))),
          getDocs(query(collection(db, 'announcements'), orderBy('createdAt', 'desc'), limit(5))),
          getDocs(query(collection(db, 'dance_courses'), orderBy('createdAt', 'desc'), limit(5)))
        ]);

        setRecentRegistrations(recentRegSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRecentAnnouncements(recentAnnSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRecentCourses(recentCourseSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const birthdaysToday = getTodaysBirthdays(usersData);
        setUpcomingBirthdays(getUpcomingBirthdays(usersData));
        setTodayBirthdays(birthdaysToday);

        // Abonnements récurrents en retard (même logique que Subscriptions.js :
        // délai de grâce de 5 jours avant de parler de retard, pour laisser le
        // temps à un prélèvement SEPA de se confirmer) — calculé depuis
        // registrationsSnapshot déjà chargé, sans requête Firestore de plus.
        const todayLocal = new Date(); todayLocal.setHours(0, 0, 0, 0);
        const overdueLimit = new Date(todayLocal); overdueLimit.setDate(overdueLimit.getDate() - 5);
        let subscriptionsCount = 0;
        let subscriptionsOverdueCount = 0;
        registrationsSnapshot.docs.forEach((d) => {
          const r = d.data();
          if (!(Number(r.installmentsTotal) > 0)) return;
          subscriptionsCount += 1;
          if (r.subscriptionStatus !== 'active' || !r.nextPaymentDate) return;
          const npd = String(r.nextPaymentDate);
          if (!/^\d{4}-\d{2}-\d{2}/.test(npd)) return;
          const [y, m, day] = npd.slice(0, 10).split('-').map(Number);
          if (new Date(y, m - 1, day) < overdueLimit) subscriptionsOverdueCount += 1;
        });

        const newStats = {
          users: usersCount,
          courses: coursesSnapshot.size,
          events: eventsSnapshot.size,
          announcements: announcementsSnapshot.size,
          resources: resourcesSnapshot.size,
          blog: blogSnapshot.size,
          teachers: teachersCount,
          students: studentsCount,
          parents: parentsCount,
          registrations: registrationsSnapshot.size,
          subscriptions: subscriptionsCount,
          subscriptionsOverdue: subscriptionsOverdueCount,
          crewAuditions: crewAuditionsSnapshot.size,
          newCrewAuditions: newCrewAuditionsSnapshot.size,
          goals: goalsSnapshot.size,
          attendance: attendanceSnapshot.size,
          shop: productsSnapshot.size,
          productOrders: productOrdersSnapshot.size,
          pendingProductOrders: pendingProductOrdersSnapshot.size,
          unreadNotifications: notificationsSnapshot.docs.filter((d) => d.data().read !== true).length,
          todayBirthdays: birthdaysToday.length,
          pendingDocumentRequests: pendingDocRequestsSnapshot.size,
          studentEvolutions: studentEvolutionsSnapshot.size
        };

        setStats(newStats);
        setContactsCount(contactsSnapshot.size);
        setQuotesCount(quotesSnapshot.size);

        const savedGroups = localStorage.getItem('dashboardGroups');
        let baseGroups = defaultGroups;

        if (savedGroups) {
          try {
            const parsed = JSON.parse(savedGroups);
            baseGroups = isValidGroups(parsed) ? mergeMissingDefaultCards(reAddIcons(parsed)) : defaultGroups;
          } catch {
            baseGroups = defaultGroups;
          }
        }

        const withCounts = applyCounts(baseGroups, newStats, contactsSnapshot.size, quotesSnapshot.size);
        setCardGroups(withCounts);
        persistGroups(withCounts);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (cardGroups.length > 0) {
      const updated = applyCounts(cardGroups, stats, contactsCount, quotesCount);
      setCardGroups(updated);
      persistGroups(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, contactsCount, quotesCount]);

  const handleEditGroup = (groupId) => {
    const group = cardGroups.find((g) => g.id === groupId);
    if (group) {
      setEditGroupDialog({ open: true, group });
      setNewGroupTitle(group.title);
    }
  };

  const handleSaveGroupTitle = () => {
    if (editGroupDialog.group && newGroupTitle.trim()) {
      const updatedGroups = cardGroups.map((group) =>
        group.id === editGroupDialog.group.id ? { ...group, title: newGroupTitle.trim() } : group
      );
      setCardGroups(updatedGroups);
      persistGroups(updatedGroups);
      setEditGroupDialog({ open: false, group: null });
      setNewGroupTitle('');
    }
  };

  const handleCreateNewGroup = () => {
    const newGroupId = `group-${Date.now()}`;
    const newGroup = { id: newGroupId, title: 'Nouveau Groupe', items: [] };
    const updatedGroups = [...cardGroups, newGroup];
    setCardGroups(updatedGroups);
    persistGroups(updatedGroups);
    setEditGroupDialog({ open: true, group: newGroup });
    setNewGroupTitle('Nouveau Groupe');
  };

  const handleDeleteGroup = () => {
    if (editGroupDialog.group) {
      const updatedGroups = cardGroups.filter((g) => g.id !== editGroupDialog.group.id);
      setCardGroups(updatedGroups);
      persistGroups(updatedGroups);
      setEditGroupDialog({ open: false, group: null });
      setNewGroupTitle('');
    }
  };

  const onCardDragStart = (groupId, itemId) => (e) => {
    dragDataRef.current = { fromGroupId: groupId, itemId };
    try {
      e.dataTransfer.setData('text/plain', JSON.stringify(dragDataRef.current));
    } catch {}
    e.dataTransfer.effectAllowed = 'move';
  };

  const onGroupDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onGroupDrop = (toGroupId) => (e) => {
    e.preventDefault();

    let payload = dragDataRef.current;

    try {
      const dt = e.dataTransfer.getData('text/plain');
      if (dt) payload = JSON.parse(dt);
    } catch {}

    if (!payload) return;

    const { fromGroupId, itemId } = payload;
    if (!fromGroupId || !itemId || fromGroupId === toGroupId) return;

    setCardGroups((prev) => {
      const next = prev.map((g) => ({ ...g, items: [...g.items] }));
      const from = next.find((g) => g.id === fromGroupId);
      const to = next.find((g) => g.id === toGroupId);

      if (!from || !to) return prev;

      const idx = from.items.findIndex((it) => it.id === itemId);
      if (idx === -1) return prev;

      const [moved] = from.items.splice(idx, 1);

      if (!to.items.find((it) => it.id === moved.id)) {
        to.items.push(moved);
      }

      const withCounts = applyCounts(next, stats, contactsCount, quotesCount);
      persistGroups(withCounts);

      return withCounts;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Chargement du tableau de bord...
        </Typography>
      </Box>
    );
  }

  return (
      <Box sx={{ flexGrow: 1, p: 3, backgroundColor: '#f8fafc' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 8 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
            Tableau de Bord
          </Typography>

          <Tooltip title="Ajouter un nouveau groupe">
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateNewGroup}
              sx={{ borderRadius: '8px', py: 1, px: 2, textTransform: 'none', fontWeight: 'bold' }}
            >
              Nouveau groupe
            </Button>
          </Tooltip>
        </Box>

        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {cardGroups.map((group) => (
              <Paper
                key={group.id}
                onDragOver={onGroupDragOver}
                onDrop={onGroupDrop(group.id)}
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  backgroundColor: 'white'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
                    {group.title}
                  </Typography>

                  <IconButton size="small" onClick={() => handleEditGroup(group.id)} aria-label="Modifier le groupe">
                    <EditIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={3}>
                  {group.items.map((card) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={card.id}>
                      <DashboardCard
                        {...card}
                        draggable
                        onDragStart={onCardDragStart(group.id, card.id)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ))}
          </Box>

          <Grid container spacing={4} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6} lg={4}>
              <Paper sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  Actions rapides
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button fullWidth variant="outlined" onClick={() => navigate('/users/new')}>
                      Nouvel utilisateur
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <Button fullWidth variant="outlined" onClick={() => navigate('/events/new')}>
                      Nouvel événement
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <Button fullWidth variant="outlined" onClick={() => navigate('/announcements/new')}>
                      Nouvelle annonce
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <Button fullWidth variant="outlined" onClick={() => navigate('/website/blog/new')}>
                      Nouvel article
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Paper sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Anniversaires
                </Typography>

                {todayBirthdays.length > 0 && (
                  <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#FDF2F8', border: '1px solid #FBCFE8' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#BE185D', mb: 1 }}>
                      🎂 Aujourd’hui
                    </Typography>

                    {todayBirthdays.map((user) => (
                      <Typography key={user.id} variant="body2" sx={{ color: '#831843' }}>
                        {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                      </Typography>
                    ))}
                  </Box>
                )}

                {upcomingBirthdays.length > 0 ? (
                  <List sx={{ py: 0 }}>
                    {upcomingBirthdays.slice(0, 5).map((user) => {
                      const bd = parseBirthDate(user.dateOfBirth);
                      const formatted = bd ? bd.toLocaleDateString('fr-BE', { day: 'numeric', month: 'long' }) : '';
                      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;

                      return (
                        <ListItem key={user.id} sx={{ py: 1 }}>
                          <ListItemAvatar>
                            <Avatar><PersonIcon /></Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            primary={fullName}
                            secondary={
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  Anniversaire : {formatted}
                                </Typography>

                                {user.role && (
                                  <Typography variant="caption" color="text.secondary">
                                    {user.role}
                                  </Typography>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Aucun anniversaire dans les 30 prochains jours
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Paper sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Activité récente
                </Typography>

                <List sx={{ py: 0 }}>
                  {recentRegistrations.slice(0, 3).map((r, idx) => (
                    <React.Fragment key={`reg-${r.id}`}>
                      <ListItem sx={{ py: 1 }}>
                        <ListItemAvatar><Avatar><HowToRegIcon /></Avatar></ListItemAvatar>
                        <ListItemText primary={r.fullName || r.name || r.email || 'Inscription'} secondary={r.courseTitle || r.course || 'Cours'} />
                      </ListItem>
                      {idx < 2 && <Divider />}
                    </React.Fragment>
                  ))}

                  {recentAnnouncements.slice(0, 2).map((a, idx) => (
                    <React.Fragment key={`ann-${a.id}`}>
                      <ListItem sx={{ py: 1 }}>
                        <ListItemAvatar><Avatar><AnnouncementsIcon /></Avatar></ListItemAvatar>
                        <ListItemText primary={a.title || 'Annonce'} secondary={a.createdAt?.toDate?.().toLocaleDateString('fr-BE') || ''} />
                      </ListItem>
                      {idx < 1 && <Divider />}
                    </React.Fragment>
                  ))}

                  {recentCourses.slice(0, 2).map((c) => (
                    <ListItem key={`course-${c.id}`} sx={{ py: 1 }}>
                      <ListItemAvatar><Avatar><CoursesIcon /></Avatar></ListItemAvatar>
                      <ListItemText primary={c.title || 'Cours'} secondary={c.category || c.teacherName || ''} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        <Dialog
          open={editGroupDialog.open}
          onClose={() => setEditGroupDialog({ open: false, group: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Modifier le groupe</DialogTitle>

          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Titre du groupe"
              fullWidth
              variant="outlined"
              value={newGroupTitle}
              onChange={(e) => setNewGroupTitle(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleDeleteGroup} color="error" disabled={cardGroups.length <= 1}>
              Supprimer
            </Button>

            <Button onClick={() => setEditGroupDialog({ open: false, group: null })}>
              Annuler
            </Button>

            <Button onClick={handleSaveGroupTitle} variant="contained">
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}

export default Dashboard;